import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { fetchAllHydraMembers } from "src/api/_common/hydra/fetchAllHydraMembers";

const CATALOG_BATCH_SIZE = 100;
const CatalogActionSchema = z.object({
    id: z.number(),
    title: z.string(),
    csrThemes: z.array(
        z.object({
            id: z.number(),
            title: z.string(),
            parent: z.object({ id: z.number(), title: z.string() }).nullish(),
        }),
    ),
});

export const useTemplateActionCatalog = (actionIds: number[], ssqVersionId?: number) => {
    const { i18n } = useTranslation();
    const ids = [...new Set(actionIds)].sort((a, b) => a - b);

    return useQuery({
        queryKey: ["templateActionCatalog", ids, ssqVersionId, i18n.resolvedLanguage],
        enabled: ids.length > 0 && ssqVersionId !== undefined,
        queryFn: async ({ signal }) => {
            const batches: number[][] = [];
            for (let offset = 0; offset < ids.length; offset += CATALOG_BATCH_SIZE) {
                batches.push(ids.slice(offset, offset + CATALOG_BATCH_SIZE));
            }

            const results = await Promise.all(
                batches.map(async (batch) =>
                    CatalogActionSchema.array().parse(
                        await fetchAllHydraMembers({
                            client: sureApiCall,
                            endpoint: "/api/actions",
                            params: {
                                ...Object.fromEntries(batch.map((id, index) => [`id[${index}]`, id])),
                                ssqVersionId,
                            },
                            requestConfig: { signal },
                        }),
                    ),
                ),
            );

            return new Map(results.flat().map((action) => [action.id, action]));
        },
    });
};
